/**
 * Translate an (method, endpoint) tuple from the activity_log into a human
 * label for the Admin Analytics activity feed.
 *
 * Four-stage resolver:
 *   1. method === 'PAGE'  → "Visited <Pretty Page Name>" (or context-specific
 *      label like "Editing order #42" for known id-suffixed routes).
 *   2. Action verbs (POST/PATCH/PUT/DELETE) → a specific past-tense label.
 *   2b. Still nothing, but the mount is known → `humaniseGeneric`, which ALWAYS
 *      answers. This is what makes a missing entry cost WORDING and never a
 *      vanished row. See its docblock.
 *   3. Anything else (GET/HEAD/OPTIONS) → null; the caller drops the row.
 *
 *   humaniseEndpoint('PAGE', '/department/orders') → 'Visited Department Orders'
 *   humaniseEndpoint('POST', '/api/orders')        → 'Submitted an order'
 *   humaniseEndpoint('POST', '/api/approvals/orders/45/approve') → 'Approved order #45'
 *   humaniseEndpoint('GET', '/api/hires/urgency-summary') → null  (dropped)
 *
 * ⚠️ THE THIRD EXAMPLE READ `humaniseEndpoint('PATCH', '/api/orders/45/approve')`
 * UNTIL 2026-09-16 AND WAS WRONG TWICE OVER — wrong method, and a route that
 * does not exist. It is the same fiction the patterns below carried, which is
 * how a docblock, a pattern list and a unit test can all agree with each other
 * and none of them with the server. Approving is
 * `POST /api/approvals/orders/:orderId/approve`.
 *
 * ── 🔻 RELOCATED 2026-09-21 FROM `client/src/utils/CLAUDE.md`, VERBATIM ─────
 * The incident narrative, moved out of that auto-loaded, byte-budgeted rule
 * document by owner ruling and kept BYTE-IDENTICAL. Every claim in it already
 * had a home in this file (the "not a route" fact directly above; the 179/232
 * measurement beside `ACTION_PATTERNS` and again at `humaniseGeneric`) — it is
 * reproduced rather than dropped because a trim that "tidies while moving" is
 * how claims disappear unnoticed. The topic file keeps the RULES and a pointer.
 *
 * 🚨 **IT MATCHED `POST /api/orders/:id/approve`, WHICH IS NOT A ROUTE** (approving is
 * `POST /api/approvals/orders/:orderId/approve`), and a `null` label is a **DROPPED ROW** — so
 * *"Approved order #45"* had never appeared on that screen, and **179 of 232 server write routes
 * produced no label at all**.
 *
 * ⚠️ Its sibling `endpointHumaniser.test.js` is a TABLE of pairs and was green throughout the defect.
 *
 * 🔒 **§3c: every `ACTION_PATTERNS` entry must match a live write route or carry `// LEGACY`** — nine did
 * not, and two of those were MISLABELLING a live route (`PATCH /api/orders/:id/cancel` read *"Edited
 * order #42"*), because a dead pattern lets a broader one answer.
 *
 * 🔑 OF THAT SENTENCE, ONLY THE COUNT **NINE** WAS HOMELESS — checked, not
 * assumed. Both mislabelling patterns are documented individually at their own
 * rows below (`/:id/cancel` and `/items/receive-all`, each reading "Edited
 * order #42"), and §3c itself is asserted by the coverage test. The count had
 * no other home in the repository, which is why the whole sentence is carried
 * here verbatim rather than left to the rows to imply.
 *
 * Kept client-side so adding new routes doesn't require a server deploy.
 * 🔒 Coverage is pinned by `endpointHumaniser.coverage.test.js`, which DERIVES
 * the pages from `App.js` and the write routes from `server/routes/` rather than
 * listing them — so it cannot inherit the wrong premise this file shipped with.
 */

import { viewTokenLabel } from '../constants/pageViewParams';

// ── Stage 1: PAGE → friendly name ────────────────────────────────────────
//
// Specific patterns first (id-suffixed routes get a context-specific label);
// the catch-all does title-case on the path segments. Path is the URL the
// user navigated to in the browser, NOT the API URL.
// 🔻 REBUILT 2026-09-16 AGAINST `client/src/App.js`, NOT AGAINST MEMORY. Three
// of the patterns here named routes THAT DO NOT EXIST — `/hires/:id`,
// `/hireforge/:id` and `/risk-assessments/:id/edit` — and a dozen live pages had
// no entry at all, so they fell to the title-case catch-all and read as
// "Visited Department / Hires". `endpointHumaniser.coverage.test.js` now
// enumerates every route in `App.js` and fails when one has no specific entry,
// so this list cannot silently go stale again.
const PAGE_PATTERNS = [
  // Orders ──────────────────────────────────────────────────────────────
  [/^\/orders\/(\d+)\/edit$/, (m) => `Editing order #${m[1]}`],
  [/^\/orders\/(\d+)$/, (m) => `Viewed order #${m[1]}`],
  [/^\/orders\/new$/, 'Opened new-order form'],
  [/^\/orders$/, 'Visited My Orders'],
  [/^\/department\/orders$/, 'Visited Department Orders'],
  [/^\/approvals$/, 'Visited Approvals'],

  // Hires ───────────────────────────────────────────────────────────────
  // 🔻 THE THREE HIRES PAGES NOW READ DIFFERENTLY — a declared contract change.
  // `/hires` used to label as the bare "Visited Hires", and the other two had no
  // entry at all, so they fell to the catch-all as "Visited Department / Hires".
  // Three separate pages reporting as one is the thing the owner is looking at.
  [/^\/hires\/(\d+)$/, (m) => `Viewed hire #${m[1]}`], // LEGACY: no such route today
  [/^\/hires$/, 'Visited My Hires'],
  [/^\/department\/hires$/, 'Visited Department Hires'],
  [/^\/admin\/hires$/, 'Visited All Hires'],

  // HireForge (marketplace) ─────────────────────────────────────────────
  [/^\/hireforge\/listings\/new$/, 'Opened new-listing form'],
  [/^\/hireforge\/listings\/(\d+)\/edit$/, (m) => `Editing listing #${m[1]}`],
  [/^\/hireforge\/listings\/(\d+)$/, (m) => `Viewed listing #${m[1]}`],
  [/^\/hireforge\/my-listings$/, 'Visited My Listings'],
  [/^\/hireforge$/, 'Browsed HireForge'],

  // Risk assessments ────────────────────────────────────────────────────
  // 🪤 The library routes must sit ABOVE `/risk-assessments/:id`, which would
  // otherwise never be reached for a numeric id — and above the bare
  // `/risk-assessments$` too, since these are longer paths.
  [/^\/risk-assessments\/library\/seed-blocks\/new$/, 'Authoring a seed block'],
  [/^\/risk-assessments\/library\/seed-blocks\/edit\/(\d+)$/, (m) => `Editing seed block #${m[1]}`],
  [/^\/risk-assessments\/library\/seed-templates\/new$/, 'Authoring a seed template'],
  [/^\/risk-assessments\/library\/seed-templates\/edit\/(\d+)$/, (m) => `Editing seed template #${m[1]}`],
  [/^\/risk-assessments\/library\/templates\/edit\/fork\/(\d+)$/, (m) => `Editing RA template #${m[1]}`],
  [/^\/risk-assessments\/library\/blocks\/new$/, 'Creating an RA block'],
  [/^\/risk-assessments\/library\/blocks\/edit\/[^/]+\/(\d+)$/, (m) => `Editing RA block #${m[1]}`],
  [/^\/risk-assessments\/library$/, 'Browsed RA Library'],
  [/^\/risk-assessments\/new$/, 'Started a risk assessment'],
  // LEGACY: `/risk-assessments/:id/edit` is not a route today. Kept — like the
  // `/api/hireforge` alternation below — because activity_log rows outlive the
  // routes that wrote them, and a stored row should still read as English.
  [/^\/risk-assessments\/(\d+)\/edit$/, (m) => `Editing RA #${m[1]}`],
  [/^\/risk-assessments\/(\d+)$/, (m) => `Viewed RA #${m[1]}`],
  [/^\/risk-assessments$/, 'Visited Risk Assessments'],

  // Planning — breakdown, teams and tasks ───────────────────────────────
  // 🪤 `/teams/breakdown`, `/teams/manage` and `/teams/my-tasks` must precede
  // `/teams/:teamId`, exactly as they do in `App.js`'s own route order.
  [/^\/teams\/breakdown$/, 'Visited Gag Breakdown'],
  [/^\/teams\/manage$/, 'Visited Manage Teams'],
  [/^\/teams\/my-tasks$/, 'Visited My Tasks'],
  [/^\/timesheets$/, 'Visited Timesheets'],
  [/^\/teams\/(\d+)$/, (m) => `Viewed team #${m[1]}`],
  [/^\/teams$/, 'Visited Planning'],
  // Legacy `/team*` redirects — still reachable from an old bookmark.
  [/^\/team\/manage$/, 'Visited Manage Teams'],
  [/^\/team(\/overview)?$/, 'Visited Planning'],

  // Budget ──────────────────────────────────────────────────────────────
  [/^\/budget\/overview$/, 'Visited Budget Overview'],
  [/^\/budget\/manage-budgets$/, 'Visited Manage Budgets'],
  [/^\/budget\/compare$/, 'Opened budget comparison'],
  [/^\/budget$/, 'Visited Budget'],

  // Grading, settings, dashboards and onboarding ────────────────────────
  [/^\/grading$/, 'Visited Grading'],
  [/^\/settings$/, 'Visited My Settings'],
  [/^\/department\/dashboard$/, 'Visited Department Dashboard'],
  [/^\/admin\/dashboard$/, 'Visited Admin Dashboard'],
  [/^\/admin\/settings$/, 'Visited Admin Settings'],
  [/^\/admin\/orders$/, 'Visited Department Orders'],
  [/^\/dashboard$/, 'Visited My Dashboard'],
  [/^\/onboarding\/new-production$/, 'Opened the setup wizard'],
  [/^\/$/, 'Visited Home'],
];

function titleCaseSegment(seg) {
  if (!seg) return '';
  return seg
    .split('-')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .join(' ');
}

/**
 * The " — To receive" half of a page label (2026-09-17).
 *
 * Owner: *"I would like page visits to also be included in activity so I can
 * tell if they are visiting which pagges, which tabs etc."*
 *
 * 🔑 IT READS THE VOCABULARY, NEVER THE TOKEN. `viewTokenLabel` returns `null`
 * for anything outside `constants/pageViewParams.js`, and this function drops a
 * null rather than falling back to the raw value — so a token that somehow
 * reached the database (a hand-rolled POST; a row written before an id was
 * retired) degrades to the PLAIN page label and never renders as free text on
 * an admin screen. **An absent label must degrade to a plain one, never to
 * silence and never to raw input.**
 */
function viewSuffixLabel(rawPath) {
  const [pathAndQuery] = String(rawPath).split(' ');
  const q = pathAndQuery.indexOf('?');
  if (q === -1) return '';
  const path = pathAndQuery.slice(0, q);
  const parts = [];
  new URLSearchParams(pathAndQuery.slice(q + 1)).forEach((value, key) => {
    const label = viewTokenLabel(path, key, value);
    if (label) parts.push(label);
  });
  return parts.length ? ' — ' + parts.join(', ') : '';
}

function humanisePage(rawPath) {
  if (!rawPath) return null;
  // Strip query string + title suffix the server may have appended.
  const path = rawPath.split(/[?\s]/)[0];
  const suffix = viewSuffixLabel(rawPath);
  for (const [pattern, label] of PAGE_PATTERNS) {
    const m = path.match(pattern);
    if (m) return (typeof label === 'function' ? label(m) : label) + suffix;
  }
  // Catch-all: title-case the segments. Strips numeric ids (so
  // /orders/42 → "Viewed Orders" if it slipped past the patterns above).
  const segs = path.split('/').filter((s) => s && !/^\d+$/.test(s));
  if (segs.length === 0) return 'Visited Home' + suffix;
  return 'Visited ' + segs.map(titleCaseSegment).join(' / ') + suffix;
}

// ── Stage 2: ACTION verb → past tense ────────────────────────────────────
//
// ─────────────────────────────────────────────────────────────────────────────
// 🚨 THE DEFECT THIS LIST SHIPPED WITH, 2026-09-16 — "Approved order #45" HAD
// NEVER ONCE APPEARED ON THE ADMIN ANALYTICS SCREEN.
// ─────────────────────────────────────────────────────────────────────────────
// The three headline entries matched `POST /api/orders/:id/approve`, `/reject`
// and `/cancel`. **None of those routes exists.** Approving an order is
// `POST /api/approvals/orders/:orderId/approve` (`server/routes/approvals.js`),
// and rejecting and overriding are its siblings. An unmatched write returns
// `null`, and `AdminAnalytics.js` DROPS every null row from the timeline — so
// the single most important action in the product was invisible, and the panel
// fell through to *"No meaningful actions recorded yet."*
//
// 🕶️ WHY IT SURVIVED: the patterns are plausible, they are the URLs you would
// guess, and nothing anywhere compared them against the routes the server really
// mounts. Measured mechanically on 2026-09-16 by enumerating every
// `router.post|put|patch|delete` under `server/routes/` and testing each against
// this list: **179 of 232 write routes produced no label at all.**
//
// 🔒 THAT CANNOT RECUR. `endpointHumaniser.coverage.test.js` re-derives the
// server's write routes from source and fails when any of them humanises to
// `null`. It is a COVERAGE test, not a list of literals, so it stays true as
// routes are added.
//
// 🔑 SPECIFIC BEATS GENERIC, AND BOTH ARE REQUIRED. The entries below are the
// actions the owner reads most, worded for a human. Everything else is caught by
// `humaniseGeneric` at the bottom of this file, which always answers — so the
// question a reviewer should ask of a new route is *"is the generic label good
// enough here?"*, never *"is it labelled at all?"*.
const ACTION_PATTERNS = [
  // Auth / account
  [/^POST \/api\/auth\/login$/, 'Signed in'],
  [/^POST \/api\/auth\/logout$/, 'Signed out'],
  [/^POST \/api\/auth\/signup$/, 'Signed up'],
  [/^POST \/api\/auth\/change-password$/, 'Changed password'],
  [/^POST \/api\/auth\/change-email$/, 'Changed email address'],
  [/^POST \/api\/auth\/forgot-password$/, 'Requested a password reset'],
  [/^POST \/api\/auth\/reset-password$/, 'Reset their password'],
  [/^POST \/api\/auth\/set-password$/, 'Set their password'],
  [/^POST \/api\/auth\/verify-email$/, 'Verified their email'],
  [/^POST \/api\/auth\/resend-verification$/, 'Resent the verification email'],
  [/^POST \/api\/auth\/admin-reset-password$/, "Reset another user's password"],
  [/^POST \/api\/auth\/impersonate$/, 'Started impersonating a user'],
  [/^POST \/api\/auth\/exit-impersonate$/, 'Stopped impersonating'],
  [/^POST \/api\/auth\/onboarding\/create-production$/, 'Created their production'],
  [/^POST \/api\/auth\/onboarding\/skip$/, 'Skipped onboarding'],

  // ── Approvals — THE ROUTES THE OLD LIST GOT WRONG ────────────────────
  [/^POST \/api\/approvals\/orders\/(\d+)\/approve/, (m) => `Approved order #${m[1]}`],
  [/^POST \/api\/approvals\/orders\/(\d+)\/reject/, (m) => `Rejected order #${m[1]}`],
  [/^POST \/api\/approvals\/orders\/(\d+)\/override/, (m) => `Skipped remaining approvals on order #${m[1]}`],
  [/^POST \/api\/approvals\/orders\/(\d+)\/initiate/, (m) => `Started the approval chain for order #${m[1]}`],
  [/^POST \/api\/approvals\/orders\/(\d+)\/validate/, (m) => `Repaired the approval chain for order #${m[1]}`],
  [/^POST \/api\/approvals\/workflows$/, 'Created an approval workflow'],
  [/^PUT \/api\/approvals\/workflows\/(\d+)/, (m) => `Edited approval workflow #${m[1]}`],
  [/^DELETE \/api\/approvals\/workflows\/(\d+)/, (m) => `Deleted approval workflow #${m[1]}`],
  [/^POST \/api\/approvals\/templates\/(\d+)\/apply/, (m) => `Applied approval template #${m[1]}`],
  [/^POST \/api\/approvals\/templates$/, 'Created an approval template'],
  [/^PUT \/api\/approvals\/templates\/(\d+)/, (m) => `Edited approval template #${m[1]}`],
  [/^DELETE \/api\/approvals\/templates\/(\d+)/, (m) => `Deleted approval template #${m[1]}`],

  // ── Orders ───────────────────────────────────────────────────────────
  // 🪤 The specific `/:id/<verb>` rows must precede the bare `PUT|PATCH
  // /api/orders/:id` catch-alls, which would otherwise swallow them.
  // 🚨 PATCH, NOT POST — CORRECTED 2026-09-16, AND THE WRONG METHOD WAS NOT A
  // COSMETIC ERROR. `router.patch('/:id/cancel')` is the real route, so this
  // entry never fired and the request fell through to the bare
  // `PATCH /api/orders/:id` catch-all below: **cancelling an order was reported
  // to the owner as "Edited order #42"**. A dead pattern does not merely fail to
  // label — it lets a broader one mislabel. Pinned by §3c of the coverage test.
  [/^PATCH \/api\/orders\/(\d+)\/cancel/, (m) => `Cancelled order #${m[1]}`],
  [/^POST \/api\/orders\/(\d+)\/duplicate/, (m) => `Duplicated order #${m[1]}`],
  [/^POST \/api\/orders\/(\d+)\/poke/, (m) => `Sent a poke about order #${m[1]}`],
  [/^POST \/api\/orders\/(\d+)\/notes/, (m) => `Saved notes on order #${m[1]}`],
  [/^POST \/api\/orders\/(\d+)\/items\/(\d+)\/notify/, (m) => `Flagged a line item on order #${m[1]}`],
  [/^DELETE \/api\/orders\/(\d+)\/items\/(\d+)\/notify/, (m) => `Cleared a line-item flag on order #${m[1]}`],
  [/^POST \/api\/orders\/(\d+)\/items\/(\d+)\/files\/claim/, (m) => `Attached a file to a line item on order #${m[1]}`],
  [/^POST \/api\/orders\/(\d+)\/items\/(\d+)\/files/, (m) => `Attached a file to a line item on order #${m[1]}`],
  [/^POST \/api\/orders\/(\d+)\/files\/claim/, (m) => `Attached a file to order #${m[1]}`],
  [/^POST \/api\/orders\/(\d+)\/files/, (m) => `Attached a file to order #${m[1]}`],
  [/^DELETE \/api\/orders\/(\d+)\/files\/(\d+)/, (m) => `Removed a file from order #${m[1]}`],
  [/^PATCH \/api\/orders\/(\d+)\/status/, (m) => `Changed the status of order #${m[1]}`],
  [/^PATCH \/api\/orders\/(\d+)\/po-number/, (m) => `Set PO on order #${m[1]}`],
  // My Orders' personal "Mark as done" / "Restore" (2026-09-30). Without these
  // the `PATCH /api/orders/:id` catch-all below read both as "Edited order #N".
  // The body (`{ done }`) is never logged, so the page sends `?done=1|0` as a
  // hint; a row without it (an API caller) gets the neutral third label.
  // ONE entry (not one per hint) so §3c's live-route probe, which carries no
  // query string, can see it.
  [/^PATCH \/api\/orders\/(\d+)\/my-done(?:\?(.*))?$/, (m) => {
    const done = new URLSearchParams(m[2] || '').get('done');
    if (done === '1') return `Marked order #${m[1]} as done`;
    if (done === '0') return `Restored order #${m[1]}`;
    return `Marked order #${m[1]} as done, or restored it`;
  }],
  [/^PATCH \/api\/orders\/(\d+)\/items\/(\d+)\/received/, (m) => `Marked a line item delivered on order #${m[1]}`],
  // 🚨 SAME DEFECT, SAME CONSEQUENCE. The real route is
  // `PATCH /:id/items/receive-all`; this said `POST .../items/received`, so
  // "Mark all Delivered" also read **"Edited order #42"**.
  [/^PATCH \/api\/orders\/(\d+)\/items\/receive-all/, (m) => `Marked every line delivered on order #${m[1]}`],
  [/^PUT \/api\/orders\/(\d+)/, (m) => `Edited order #${m[1]}`],
  [/^PATCH \/api\/orders\/(\d+)/, (m) => `Edited order #${m[1]}`],
  // 🪤 ANCHORED. Unanchored it swallowed `DELETE /api/orders/:id/items/:itemId/notify`
  // — which CLEARS A LINE-ITEM FLAG — and reported "Deleted order #42". The order
  // is still there; the label says it is not.
  [/^DELETE \/api\/orders\/(\d+)$/, (m) => `Deleted order #${m[1]}`],
  [/^POST \/api\/orders$/, 'Submitted an order'],
  [/^POST \/api\/order-uploads\/pending\/discard$/, 'Discarded a pending upload'],
  [/^POST \/api\/order-uploads\/pending$/, 'Uploaded an order attachment'],

  // ── Hires ────────────────────────────────────────────────────────────
  [/^POST \/api\/hires$/, 'Logged a hire'], // LEGACY: hires are created as order LINES; routes/hires.js has only the five PATCHes below
  [/^PATCH \/api\/hires\/(\d+)\/extend/, (m) => `Extended hire #${m[1]}`],
  [/^PATCH \/api\/hires\/(\d+)\/shorten/, (m) => `Shortened hire #${m[1]}`],
  [/^PATCH \/api\/hires\/(\d+)\/change-start/, (m) => `Changed the start date of hire #${m[1]}`],
  [/^PATCH \/api\/hires\/(\d+)\/off-hire/, (m) => `Off-hired #${m[1]}`],
  [/^PATCH \/api\/hires\/(\d+)\/re-hire/, (m) => `Re-hired #${m[1]}`],
  [/^POST \/api\/hires\/orders\/(\d+)\/off-hire-all/, (m) => `Off-hired every hire on order #${m[1]}`], // 2026-09-28, one transaction
  [/^POST \/api\/hires\/orders\/(\d+)\/extend-all/, (m) => `Extended every hire on order #${m[1]}`], // 2026-09-28, one transaction
  [/^POST \/api\/hires\/orders\/(\d+)\/re-hire-all/, (m) => `Brought every hire on order #${m[1]} back on hire`], // 2026-09-28, one transaction
  [/^PATCH \/api\/hires\/(\d+)\/status/, (m) => `Updated hire #${m[1]} status`], // LEGACY: no /status route; status moves via off-hire / re-hire
  [/^PUT \/api\/hires\/(\d+)/, (m) => `Edited hire #${m[1]}`], // LEGACY: no PUT; edits go through extend / shorten / change-start
  [/^DELETE \/api\/hires\/(\d+)/, (m) => `Deleted hire #${m[1]}`], // LEGACY: no DELETE; a hire line is removed with its order item

  // ── Budget ───────────────────────────────────────────────────────────
  [/^POST \/api\/budget\/versions\/(\d+)\/set-current/, (m) => `Made budget version #${m[1]} current`],
  [/^POST \/api\/budget\/versions\/(\d+)\/set-baseline/, (m) => `Set budget version #${m[1]} as the baseline`],
  [/^POST \/api\/budget\/versions\/(\d+)\/lock/, (m) => `Locked budget version #${m[1]}`],
  [/^POST \/api\/budget\/versions\/(\d+)\/unlock/, (m) => `Unlocked budget version #${m[1]}`],
  [/^POST \/api\/budget\/versions\/(\d+)\/clone/, (m) => `Cloned budget version #${m[1]}`],
  [/^POST \/api\/budget\/versions$/, 'Created a budget version'],
  [/^PUT \/api\/budget\/versions\/(\d+)/, (m) => `Renamed budget version #${m[1]}`],
  [/^DELETE \/api\/budget\/versions\/(\d+)/, (m) => `Deleted budget version #${m[1]}`],
  [/^PATCH \/api\/budget\/cost-code-budgets\/spend-adjustment$/, 'Adjusted spend on a cost code'],
  [/^PUT \/api\/budget\/cost-code-budgets$/, 'Saved cost-code budgets'],
  [/^PUT \/api\/budget\/crew-members\/(\d+)\/weeks/, (m) => `Changed the schedule of crew member #${m[1]}`],
  [/^PUT \/api\/budget\/crew-members\/(\d+)\/days/, (m) => `Changed the day grid of crew member #${m[1]}`],
  [/^POST \/api\/budget\/crew-members\/(\d+)\/cost-preview/, 'Previewed a crew cost'],
  [/^POST \/api\/budget\/crew-members$/, 'Added a crew member'],
  [/^PUT \/api\/budget\/crew-members\/(\d+)/, (m) => `Edited crew member #${m[1]}`],
  [/^DELETE \/api\/budget\/crew-members\/(\d+)/, (m) => `Removed crew member #${m[1]}`],

  // ── Gag breakdown ────────────────────────────────────────────────────
  [/^POST \/api\/gags\/(\d+)\/push-to-budget/, (m) => `Pushed gag #${m[1]} to the budget`],
  [/^POST \/api\/gags\/(\d+)\/create-team/, (m) => `Created a team for gag #${m[1]}`],
  [/^POST \/api\/gags\/(\d+)\/take/, (m) => `Took ownership of gag #${m[1]}`],
  [/^POST \/api\/gags\/(\d+)\/release/, (m) => `Released gag #${m[1]}`],
  [/^POST \/api\/gags\/(\d+)\/assign/, (m) => `Assigned gag #${m[1]}`],
  [/^POST \/api\/gags\/(\d+)\/restore/, (m) => `Restored gag #${m[1]}`],
  [/^POST \/api\/gags\/(\d+)\/risk-assessments/, (m) => `Linked a risk assessment to gag #${m[1]}`],
  [/^DELETE \/api\/gags\/(\d+)\/risk-assessments\/(\d+)/, (m) => `Unlinked a risk assessment from gag #${m[1]}`],
  [/^PATCH \/api\/gags\/(\d+)\/tasks\/(\d+)\/status/, (m) => `Moved a task on gag #${m[1]}`],
  [/^POST \/api\/gags\/(\d+)\/tasks/, (m) => `Added a task to gag #${m[1]}`],
  [/^PUT \/api\/gags\/(\d+)\/tasks\/(\d+)/, (m) => `Edited a task on gag #${m[1]}`],
  [/^DELETE \/api\/gags\/(\d+)\/tasks\/(\d+)/, (m) => `Deleted a task on gag #${m[1]}`],
  [/^PATCH \/api\/gags\/(\d+)\/dates/, (m) => `Changed the dates on gag #${m[1]}`],
  [/^POST \/api\/gags$/, 'Created a gag'],
  [/^PUT \/api\/gags\/(\d+)/, (m) => `Edited gag #${m[1]}`],
  [/^DELETE \/api\/gags\/(\d+)/, (m) => `Deleted gag #${m[1]}`],

  // ── Teams and tasks ──────────────────────────────────────────────────
  [/^POST \/api\/teams\/assign-task$/, 'Assigned a task'],
  [/^POST \/api\/teams\/invites\/(\d+)\/accept/, 'Accepted a team invitation'],
  [/^POST \/api\/teams\/invites\/(\d+)\/decline/, 'Declined a team invitation'],
  [/^DELETE \/api\/teams\/my-data$/, 'Deleted their own team data'],
  [/^PATCH \/api\/teams\/(\d+)\/tasks\/(\d+)\/status/, (m) => `Moved a task in team #${m[1]}`],
  [/^POST \/api\/teams\/(\d+)\/tasks/, (m) => `Added task to team #${m[1]}`],
  [/^DELETE \/api\/teams\/(\d+)\/tasks\/(\d+)\/files\/(\d+)/, (m) => `Removed a task file in team #${m[1]}`],
  [/^DELETE \/api\/teams\/(\d+)\/tasks\/(\d+)\/assign\/(\d+)/, (m) => `Unassigned someone from a task in team #${m[1]}`],
  // 🔻 TWO PATTERNS DELETED HERE 2026-09-16, NOT ANCHORED — and the choice is the
  // point. They were `PUT|DELETE /api/teams/:t/tasks/:id` UNANCHORED, so they
  // also swallowed `…/tasks/:id/comments/:cid`: deleting or editing a COMMENT
  // reported "Deleted a task in team #42" / "Edited a task in team #42". A
  // previously-dropped row turned into a FALSE STATEMENT about a task that is
  // still there — strictly worse than the silence, because it reads as a fact.
  //
  // 🔑 ANCHORING THEM WOULD HAVE WORKED AND WAS STILL THE WRONG FIX: measured,
  // `humaniseGeneric` already answers BETTER than the specific entries did for
  // all four shapes — "Deleted task #7", "Updated task #7", "Deleted comment #9",
  // "Updated comment #9", each naming the thing actually touched and carrying ITS
  // id rather than the team's. Keeping a specific pattern that is beaten by the
  // fallback is just a second, worse answer to a question that has one.
  // ⚠️ `assign` needs the entry above because the generic reads the tail verb and
  // says "Assigned", which is the OPPOSITE of what a DELETE there does.
  // ⚠️ The `files` entry above stays: it precedes these shapes and names the team.
  [/^POST \/api\/teams\/(\d+)\/invite/, (m) => `Invited someone to team #${m[1]}`], // LEGACY: invites are POST /api/teams/:id/members + the invites/:id/accept pair
  [/^POST \/api\/teams\/(\d+)\/members/, (m) => `Added a member to team #${m[1]}`],
  [/^DELETE \/api\/teams\/(\d+)\/members\/(\d+)/, (m) => `Removed a member from team #${m[1]}`],
  [/^POST \/api\/teams$/, 'Created a team'],
  [/^PUT \/api\/teams\/(\d+)$/, (m) => `Renamed team #${m[1]}`],
  [/^DELETE \/api\/teams\/(\d+)$/, (m) => `Deleted team #${m[1]}`],

  // ── Department files ─────────────────────────────────────────────────
  [/^POST \/api\/files\/folders\/(\d+)\/files/, 'Uploaded a file'],
  [/^POST \/api\/files\/departments\/(\d+)\/folders/, 'Created a folder'],
  [/^POST \/api\/files\/files\/(\d+)\/lock\/break/, 'Broke a file lock'],
  [/^POST \/api\/files\/files\/(\d+)\/lock/, 'Locked a file'],
  [/^POST \/api\/files\/files\/(\d+)\/unlock/, 'Unlocked a file'],
  [/^POST \/api\/files\/files\/(\d+)\/restore/, 'Restored a file'],
  [/^PUT \/api\/files\/files\/(\d+)\/content/, 'Saved a file'],
  [/^DELETE \/api\/files\/files\/(\d+)/, 'Deleted a file'],
  [/^POST \/api\/files\/folders\/(\d+)\/restore/, 'Restored a folder'],
  [/^PATCH \/api\/files\/folders\/(\d+)/, 'Renamed a folder'],
  [/^DELETE \/api\/files\/folders\/(\d+)/, 'Deleted a folder'],

  // ── HireForge / marketplace ──────────────────────────────────────────
  // Matches both prefixes so rows recorded under the historical
  // `/api/hireforge` mount still humanise.
  [/^POST \/api\/(?:hireforge|marketplace)\/listings\/(\d+)\/images/, (m) => `Added a photo to listing #${m[1]}`],
  [/^PUT \/api\/(?:hireforge|marketplace)\/listings\/(\d+)\/images\/reorder/, (m) => `Reordered photos on listing #${m[1]}`],
  [/^PATCH \/api\/(?:hireforge|marketplace)\/listings\/(\d+)\/status/, (m) => `Changed the status of listing #${m[1]}`],
  [/^POST \/api\/(?:hireforge|marketplace)\/listings$/, 'Created a marketplace listing'],
  [/^PUT \/api\/(?:hireforge|marketplace)\/listings\/(\d+)/, (m) => `Edited listing #${m[1]}`],
  [/^DELETE \/api\/(?:hireforge|marketplace)\/listings\/(\d+)\/images\/(\d+)/, (m) => `Removed a photo from listing #${m[1]}`],
  // 🪤 ANCHORED. Unanchored it swallowed the images DELETE above and reported
  // removing ONE PHOTO as "Removed listing #42" — the listing is still live.
  [/^DELETE \/api\/(?:hireforge|marketplace)\/listings\/(\d+)$/, (m) => `Removed listing #${m[1]}`],

  // ── Risk assessments ─────────────────────────────────────────────────
  [/^POST \/api\/risk-assessments\/(\d+)\/sign-off/, (m) => `Signed off RA #${m[1]}`],
  [/^POST \/api\/risk-assessments\/(\d+)\/sections\/(\d+)\/rows/, (m) => `Added a row to RA #${m[1]}`],
  [/^POST \/api\/risk-assessments\/(\d+)\/sections\/(\d+)\/pull-latest/, (m) => `Pulled the latest block into RA #${m[1]}`],
  // 🪤 ANCHORED. Unanchored it swallowed both routes above, so adding a single
  // ROW — and pulling a block update — both reported "Added a section to RA #42".
  [/^POST \/api\/risk-assessments\/(\d+)\/sections$/, (m) => `Added a section to RA #${m[1]}`],
  [/^POST \/api\/risk-assessments\/(\d+)\/revise/, (m) => `Started a revision of RA #${m[1]}`],
  [/^POST \/api\/risk-assessments\/(\d+)\/make-current/, (m) => `Made RA #${m[1]} the current version`],
  [/^POST \/api\/risk-assessments\/(\d+)\/clone/, (m) => `Cloned RA #${m[1]}`],
  [/^POST \/api\/risk-assessments\/(\d+)\/create-template/, (m) => `Created a template from RA #${m[1]}`],
  [/^POST \/api\/risk-assessments\/(\d+)\/regs\/reset/, (m) => `Reset the regulations on RA #${m[1]}`],
  [/^PUT \/api\/risk-assessments\/(\d+)\/rows\//, (m) => `Edited a row in RA #${m[1]}`],
  [/^PUT \/api\/risk-assessments\/(\d+)$/, (m) => `Edited RA #${m[1]}`],
  [/^DELETE \/api\/risk-assessments\/(\d+)$/, (m) => `Deleted RA #${m[1]}`],
  [/^POST \/api\/risk-assessments$/, 'Created a risk assessment'],
  [/^(?:POST|PUT) \/api\/risk-assessments\/(?:seed-)?blocks/, 'Saved an RA block'],
  [/^DELETE \/api\/risk-assessments\/(?:seed-)?blocks/, 'Deleted an RA block'],
  [/^(?:POST|PUT) \/api\/risk-assessments\/(?:seed-)?templates/, 'Saved an RA template'],
  [/^DELETE \/api\/risk-assessments\/(?:seed-)?templates/, 'Deleted an RA template'],

  // ── Grading ──────────────────────────────────────────────────────────
  [/^POST \/api\/grading\/me\/request-approval/, 'Requested grading reference'], // LEGACY: now POST /api/grading/entries/:entryId/request-approval
  [/^POST \/api\/grading\/me\/attachments/, 'Uploaded grading document'],
  [/^DELETE \/api\/grading\/me\/attachments\/(\d+)/, 'Removed grading document'],
  [/^POST \/api\/grading\/entries\/(\d+)\/request-approval/, 'Requested a grading reference'],
  [/^POST \/api\/grading\/entries\/(\d+)\/withdraw/, 'Withdrew a grading reference request'],
  [/^(?:PATCH|PUT) \/api\/grading\/me/, 'Saved grading profile'],
  [/^POST \/api\/grading-approval\/[^/]+$/, 'Answered a grading reference request'],

  // ── People, departments and settings ─────────────────────────────────
  [/^POST \/api\/productions$/, 'Created a production'],
  [/^PUT \/api\/productions\/(\d+)/, (m) => `Renamed production #${m[1]}`],
  [/^POST \/api\/departments$/, 'Created a department'],
  [/^PUT \/api\/departments\/(\d+)\/members\/(\d+)/, (m) => `Changed a member's role in department #${m[1]}`],
  [/^POST \/api\/departments\/(\d+)\/members/, (m) => `Added someone to department #${m[1]}`],
  [/^DELETE \/api\/departments\/(\d+)\/leave$/, (m) => `Left department #${m[1]}`],
  [/^DELETE \/api\/departments\/(\d+)\/(?:members|assignments)\/(\d+)/, (m) => `Removed someone from department #${m[1]}`],
  [/^POST \/api\/departments\/production\/(\d+)\/remove-member/, 'Removed someone from a production'],
  [/^PUT \/api\/departments\/(\d+)$/, (m) => `Edited department #${m[1]}`],
  [/^DELETE \/api\/departments\/(\d+)$/, (m) => `Deleted department #${m[1]}`],
  [/^POST \/api\/sub-departments$/, 'Created a sub-department'],
  [/^(?:PUT|DELETE) \/api\/sub-departments/, 'Edited a sub-department'],
  [/^(?:PUT|DELETE) \/api\/roles\/user-assignments\/(\d+)/, (m) => `Changed the role of user #${m[1]}`],
  [/^POST \/api\/roles$/, 'Created a role'],
  [/^PUT \/api\/roles\/(\d+)/, 'Edited a role'],
  [/^DELETE \/api\/roles\/(\d+)/, 'Deleted a role'],
  [/^POST \/api\/users\/(\d+)\/offboard/, (m) => `Offboarded user #${m[1]}`],
  [/^POST \/api\/users\/(\d+)\/invite/, (m) => `Invited user #${m[1]}`],
  [/^POST \/api\/users\/(\d+)\/link-crew/, 'Linked a crew record to a user'],
  [/^DELETE \/api\/users\/(\d+)\/link-crew/, 'Unlinked a crew record from a user'],
  [/^PATCH \/api\/users\/(\d+)\/status/, (m) => `Changed the status of user #${m[1]}`],
  [/^POST \/api\/users\/invite/, 'Invited a new user'], // LEGACY: now POST /api/users/:id/invite, matched by the entry above
  [/^PUT \/api\/users\/me$/, 'Updated their own profile'],
  [/^POST \/api\/users$/, 'Created a user'],
  [/^PUT \/api\/users\/(\d+)/, (m) => `Edited user #${m[1]}`],
  [/^DELETE \/api\/users\/(\d+)/, (m) => `Deleted user #${m[1]}`],
  [/^POST \/api\/invitations$/, 'Sent an invitation'],
  [/^DELETE \/api\/invitations\/(\d+)/, 'Revoked an invitation'],
  [/^POST \/api\/department-invites\/(\d+)\/accept$/, 'Joined a department from an invitation'],
  [/^POST \/api\/department-invites\/(\d+)\/withdraw$/, 'Withdrew a department invitation'],
  [/^POST \/api\/suppliers$/, 'Added a supplier'],
  [/^PUT \/api\/suppliers\/(\d+)/, 'Edited a supplier'],
  [/^DELETE \/api\/suppliers\/(\d+)/, 'Deleted a supplier'],

  // ── Notifications and backup ─────────────────────────────────────────
  [/^POST \/api\/notifications\/mark-all-read$/, 'Cleared their notifications'],
  [/^POST \/api\/notifications\/admin\/mark-read$/, 'Cleared an order alert'],
  [/^PATCH \/api\/notifications\/(\d+)\/read/, 'Read a notification'],
  [/^PUT \/api\/notifications\/mute-preferences/, 'Changed their notification settings'],
  [/^POST \/api\/backup\/department\/restore$/, 'Restored a department backup'],
  [/^POST \/api\/backup\/restore$/, 'Restored a whole-instance backup'],
  [/^POST \/api\/backup\/scheduled\/run$/, 'Ran a backup'],
];

// ── Stage 2b: the GENERIC fallback — why no write can be unlabelled ──────
//
// 🔑 THE POINT OF THIS STAGE IS THAT IT ALWAYS ANSWERS. Before it existed, a
// write route with no entry in `ACTION_PATTERNS` returned `null` and
// `AdminAnalytics.js` silently dropped the row — so the failure mode of an
// out-of-date map was INVISIBLE DATA, not an ugly label. 179 of 232 routes were
// in that state on 2026-09-16. A plain-but-honest label is strictly better than
// a vanished one, and it degrades gracefully as routes are added.
//
// 🚨 HELD IN STEP WITH THE SERVER BY A PARITY TEST. These keys must match
// `API_PAGE_LABELS` in `server/helpers/analyticsEndpointMap.js` exactly —
// `endpointHumaniser.coverage.test.js` reads that file and fails when they
// diverge, the device `upload-blocklist-client-parity` already uses. Edit one
// and CI makes you edit the other.
const API_AREA_LABELS = {
  '/api/analytics': 'Analytics',
  '/api/approvals': 'Approvals',
  '/api/auth': 'Account',
  '/api/backup': 'Backup',
  '/api/budget': 'Budget',
  '/api/dashboard': 'Dashboard',
  '/api/departments': 'Departments',
  '/api/files': 'Files',
  '/api/gags': 'Breakdown',
  '/api/tasks': 'My Tasks',
  '/api/timesheets': 'Timesheets',
  '/api/grading': 'Grading',
  '/api/grading-approval': 'Grading',
  '/api/hires': 'Hires',
  '/api/invitations': 'Invitations',
  '/api/department-invites': 'Invitations',
  '/api/marketplace': 'HireForge',
  '/api/notifications': 'Notifications',
  '/api/order-uploads': 'Orders',
  '/api/orders': 'Orders',
  '/api/productions': 'Productions',
  '/api/risk-assessments': 'Risk Assessments',
  '/api/roles': 'Roles',
  '/api/sub-departments': 'Sub-Departments',
  '/api/suppliers': 'Suppliers',
  '/api/teams': 'Teams',
  '/api/users': 'Users',
  '/api/health': 'Health Check',
  '/api/schema': 'Schema',
  '/uploads': 'Files',
};

// A trailing path segment that names an ACTION rather than a resource. Without
// this, `POST /api/gags/42/take` would read "Created take" — the verb comes from
// the path, not from the HTTP method, whenever the path supplies one.
const TAIL_ACTION_VERBS = {
  accept: 'Accepted', apply: 'Applied', approve: 'Approved', assign: 'Assigned',
  break: 'Broke', cancel: 'Cancelled', claim: 'Claimed', clone: 'Cloned',
  decline: 'Declined', discard: 'Discarded', duplicate: 'Duplicated',
  extend: 'Extended', initiate: 'Started', invite: 'Invited', leave: 'Left',
  lock: 'Locked', offboard: 'Offboarded', override: 'Overrode', poke: 'Poked',
  reactivate: 'Reactivated', received: 'Received', reject: 'Rejected', 'release': 'Released',
  reorder: 'Reordered', reset: 'Reset', restore: 'Restored', revise: 'Revised',
  'set-current': 'Made current', 'sign-off': 'Signed off', skip: 'Skipped',
  take: 'Took', unlock: 'Unlocked', validate: 'Validated', withdraw: 'Withdrew',
};

const METHOD_VERBS = { POST: 'Created', PUT: 'Updated', PATCH: 'Updated', DELETE: 'Deleted' };

/** `crew-members` → `crew member`; `categories` → `category`. */
function singularise(segment) {
  const words = segment.replace(/-/g, ' ');
  if (/ies$/.test(words)) return words.replace(/ies$/, 'y');
  if (/ss$/.test(words)) return words;
  return words.replace(/s$/, '');
}

// 🚨 A PATH SEGMENT IS UNTRUSTED TEXT, AND THIS FUNCTION PUTS IT ON A SCREEN.
// `humaniseGeneric` names the noun after the last non-numeric segment, which is
// a ROUTE WORD on every route that exists today — but a route param that is not
// an integer lands here verbatim. `POST /api/grading-approval/:token` already
// has that shape, and a future `/api/users/:email/...` would put
// "Created paul@example.com" in the owner's activity feed: an identifier
// leaking out of a URL into a rendered label, which is neither useful nor
// something anybody chose to display.
// 🔑 The guard is deliberately CRUDE and fails towards a NEUTRAL NOUN rather
// than towards the raw text — a vague label costs wording, an echoed token
// costs a disclosure. Anything with an `@`, a `%` (percent-encoding), a `.`
// (emails, filenames), whitespace, or simply too long to be a route word, is
// not a route word.
const MAX_NOUN_SEGMENT = 24;
function isRouteWord(segment) {
  if (!segment || segment.length > MAX_NOUN_SEGMENT) return false;
  return /^[A-Za-z][A-Za-z-]*$/.test(segment);
}

function humaniseGeneric(method, endpoint) {
  const path = String(endpoint).split(/[?\s]/)[0];
  const segs = path.split('/').filter(Boolean);
  if (segs[0] !== 'api' || segs.length < 2) return null;
  const area = API_AREA_LABELS['/api/' + segs[1]];
  if (!area) return null;

  const isId = (s) => /^\d+$/.test(s);
  // 🚨 ONLY ROUTE WORDS MAY BECOME THE NOUN — see `isRouteWord`. A `:token` or a
  // `:email` param is neither an id nor a route word, so it is dropped here
  // rather than being echoed into a label on the owner's screen.
  const named = segs.slice(2).filter((s) => !isId(s) && isRouteWord(s));
  const tail = named.length ? named[named.length - 1] : null;
  const tailVerb = tail ? TAIL_ACTION_VERBS[tail] : null;

  // The noun is the COLLECTION being acted on. With an action word in the tail
  // the collection is the segment before it (or the mount when the action hangs
  // straight off the mount, as in `/api/gags/42/take`).
  const nounSeg = tailVerb
    ? (named.length > 1 ? named[named.length - 2] : segs[1])
    : (tail || segs[1]);
  const verb = tailVerb || METHOD_VERBS[String(method).toUpperCase()] || 'Changed';

  // 🪤 THE ID MUST BE THE NOUN'S OWN ID, NEVER "the last number in the path".
  // `POST /api/files/departments/1/folders` creates a folder IN department 1 —
  // labelling it "Created folder #1" invents a folder id out of a department id,
  // which is worse than saying nothing, because it reads as a fact. So the id is
  // taken only from the segment IMMEDIATELY AFTER the noun.
  // 🪤 `lastIndexOf`, not `indexOf`: `/api/files/files/42` repeats the segment,
  // and `indexOf` would find the MOUNT and look for an id at `files`.
  const nounAt = segs.lastIndexOf(nounSeg);
  const after = nounAt >= 0 ? segs[nounAt + 1] : null;
  const id = after && isId(after) ? after : null;

  // 🔑 NO AREA SUFFIX. It was tried and removed the same day: on the routes that
  // reach this fallback the noun already names the area ("Updated crew member
  // #42"), so "(Budget)" is noise, and on the ones it would disambiguate the
  // specific patterns above already win.
  return `${verb} ${singularise(nounSeg)}${id ? ` #${id}` : ''}`;
}

function humaniseAction(method, endpoint) {
  if (!method || !endpoint) return null;
  const key = `${method.toUpperCase()} ${endpoint}`;
  for (const [pattern, label] of ACTION_PATTERNS) {
    const m = key.match(pattern);
    if (m) return typeof label === 'function' ? label(m) : label;
  }
  // 🔑 Never `null` for a write to a known area — see the stage-2b docblock.
  return humaniseGeneric(method, endpoint);
}

/**
 * Resolve a label or null. Returning null tells the caller to DROP the row
 * — the feed should never show raw "GET /api/x/y" strings.
 */
export { PAGE_PATTERNS, ACTION_PATTERNS, API_AREA_LABELS, humaniseGeneric };

/**
 * "Changed the status of order #11347" → "Changed the status of order ORD-012
 * (Rigging)" — the number people can look up, not the database id (owner
 * 2026-10-14). `orderRef` is the server's `order_ref` on an analytics row
 * (`{ number, department }`, or null); with none the label is left as it is.
 */
export function withOrderNumber(label, orderRef) {
  if (!label || !orderRef || !orderRef.number) return label;
  const ref = orderRef.department ? `${orderRef.number} (${orderRef.department})` : orderRef.number;
  return label.replace(/\border #\d+/, `order ${ref}`);
}

export function humaniseEndpoint(method, endpoint) {
  if (!method || !endpoint) return null;
  const m = String(method).toUpperCase();
  if (m === 'PAGE') return humanisePage(endpoint);
  if (m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE') {
    return humaniseAction(m, endpoint);
  }
  // GET / HEAD / OPTIONS etc → drop. Page navigations are captured via the
  // PAGE branch; raw GETs are background polling noise we don't want.
  return null;
}
